import { useState, useEffect } from "react";
import Cookies from "js-cookie";

export const useUIState = (groupedDevices) => {
  // UI State
  const [popupOpen, setPopupOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showMobileDevices, setShowMobileDevices] = useState(false);
  const [listSelect, setListSelect] = useState(false);

  // Map state
  const _initialMapCenter = {
    lat: 29.8105572,
    lng: -98.2212979,
  };
  const _initialMapZoom = 11;

  const [mapCenter, setMapCenter] = useState(_initialMapCenter);
  const [mapZoom, setMapZoom] = useState(_initialMapZoom);

  // Settings and login state
  const [showSettingsState, setShowSettingsState] = useState(false);
  const [loginPopupOpen, setLoginPopupOpen] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loginTrigger, setLoginTrigger] = useState(false);
  const [explodedGroups, setExplodedGroups] = useState(new Set());

  // Initialize collapsed categories based on groupedDevices
  const [collapsedCategories, setCollapsedCategories] = useState(() =>
    Object.keys(groupedDevices).reduce((acc, category) => {
      acc[category] = false;
      return acc;
    }, {})
  );

  // Initialize collapsed groups - start with all groups collapsed
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const initialCollapsedState = {};

    Object.keys(groupedDevices).forEach((category) => {
      groupedDevices[category].forEach((group) => {
        const groupKey = `${category}-${group.id}`;
        initialCollapsedState[groupKey] = true; // true means collapsed
      });
    });

    return initialCollapsedState;
  });

  // Effect to update collapsed states when groupedDevices changes
  useEffect(() => {
    // Update collapsed categories for any new categories
    setCollapsedCategories((prev) => {
      let changed = false;
      const newCategories = { ...prev };
      Object.keys(groupedDevices).forEach((category) => {
        if (!(category in newCategories)) {
          newCategories[category] = false;
          changed = true;
        }
      });
      // Remove categories that no longer exist
      Object.keys(newCategories).forEach((category) => {
        if (!(category in groupedDevices)) {
          delete newCategories[category];
          changed = true;
        }
      });
      return changed ? newCategories : prev;
    });

    // Update collapsed groups for any new groups
    setCollapsedGroups((prev) => {
      let changed = false;
      const newCollapsedGroups = { ...prev };
      Object.keys(groupedDevices).forEach((category) => {
        groupedDevices[category].forEach((group) => {
          const groupKey = `${category}-${group.id}`;
          if (!(groupKey in newCollapsedGroups)) {
            newCollapsedGroups[groupKey] = true;
            changed = true;
          }
        });
      });
      // Remove groups that no longer exist
      Object.keys(newCollapsedGroups).forEach((groupKey) => {
        const [category, ...idParts] = groupKey.split("-");
        const id = idParts.join("-");
        const exists = groupedDevices[category]?.some(
          (g) => String(g.id) === id
        );
        if (!exists) {
          delete newCollapsedGroups[groupKey];
          changed = true;
        }
      });
      return changed ? newCollapsedGroups : prev;
    });
  }, [groupedDevices]);

  const calculateBounds = (devices) => {
    if (!devices || devices.length === 0) return null;

    // For single device, just center on it with a reasonable zoom
    if (devices.length === 1) {
      const location = JSON.parse(devices[0].location);
      return {
        center: location,
        zoom: 15, // Reduced from 16 for single devices
      };
    }

    // For multiple devices, calculate bounding box
    const locations = devices.map((device) => JSON.parse(device.location));

    const lats = locations.map((loc) => loc.lat);
    const lngs = locations.map((loc) => loc.lng);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Calculate center point
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    // Calculate the distance between the furthest points
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;

    // Use the larger difference to determine zoom
    const maxDiff = Math.max(latDiff, lngDiff);

    // Add padding to ensure all devices are visible with some margin
    const paddingFactor = 1.5; // 50% padding around the bounding box
    const paddedDiff = maxDiff * paddingFactor;

    // More conservative zoom calculation with better spacing
    let zoom = 18; // Default max zoom

    if (paddedDiff > 1.0) zoom = 8; // Very far apart (city level)
    else if (paddedDiff > 0.5) zoom = 9; // Far apart (large area)
    else if (paddedDiff > 0.2) zoom = 10; // Medium-far distance
    else if (paddedDiff > 0.1) zoom = 11; // Medium distance
    else if (paddedDiff > 0.05) zoom = 12; // Close distance
    else if (paddedDiff > 0.02) zoom = 13; // Very close
    else if (paddedDiff > 0.01) zoom = 14; // Almost same area
    else if (paddedDiff > 0.005) zoom = 15; // Nearly identical
    else zoom = 16; // Essentially same location

    console.log(`Calculating bounds for ${devices.length} devices:`, {
      latDiff,
      lngDiff,
      maxDiff,
      paddedDiff,
      zoom,
      center: { lat: centerLat, lng: centerLng },
      deviceLocations: locations,
    });

    return {
      center: { lat: centerLat, lng: centerLng },
      zoom: zoom,
    };
  };

  const toggleGroupExploded = (groupKey) => {
    setExplodedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const triggerLoginOpen = () => {
    console.log("triggerLoginOpen");
    setLoginTrigger(true);
    setTimeout(() => setLoginTrigger(false), 1000);
  };

  const toggleCategoryCollapse = (category) => {
    setCollapsedCategories({
      ...collapsedCategories,
      [category]: !collapsedCategories[category],
    });
  };

  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups({
      ...collapsedGroups,
      [groupKey]: !collapsedGroups[groupKey],
    });
  };

  const setMultipleGroupsCollapse = (newCollapsedState) => {
    setCollapsedGroups(newCollapsedState);
  };

  const handleLogout = (auth) => {
    auth.setAuth(null);
    Cookies.remove("accessToken");
    Cookies.remove("email");
    setShowUserMenu(false);
  };

  return {
    // UI State
    popupOpen,
    setPopupOpen,
    showUserMenu,
    setShowUserMenu,
    showTermsOfUse,
    setShowTermsOfUse,
    showPrivacyPolicy,
    setShowPrivacyPolicy,
    showMobileDevices,
    setShowMobileDevices,
    listSelect,
    setListSelect,

    // Map state
    _initialMapCenter,
    _initialMapZoom,
    mapCenter,
    setMapCenter,
    mapZoom,
    setMapZoom,

    // Settings and login state
    showSettingsState,
    setShowSettingsState,
    loginPopupOpen,
    setLoginPopupOpen,
    error,
    setError,
    success,
    setSuccess,
    loginTrigger,
    setLoginTrigger,

    // Collapse states
    collapsedGroups,
    setCollapsedGroups,
    collapsedCategories,
    setCollapsedCategories,

    // Functions
    triggerLoginOpen,
    toggleCategoryCollapse,
    toggleGroupCollapse,
    handleLogout,

    // Exploded groups
    explodedGroups,
    setExplodedGroups,
    toggleGroupExploded,
    calculateBounds,
    setMultipleGroupsCollapse,
  };
};
